/**
 * MealtimeStepList — the mealtime "steps" of the unified production-record
 * input (PROD-1279 B1). Each step is one mealtime's production record (or the
 * trucks of a mealtime with no record yet); the rail shows a check for a
 * signed one, a live "filled / total" count for the rest, and highlights the
 * active one. The user picks a step; nothing advances on its own.
 *
 * A thin adapter over the shared `WorkflowStepList` rail, exactly as the chef's
 * `LocationStepList` is ("LOCATIONS" there, "MEALTIMES" here). Vertical for the
 * desktop left rail, horizontal for the mobile chip strip.
 */

import { WorkflowStepList } from '@ordo/ui/workflow-step-list'

export interface MealtimeStepListItem {
  key: string
  title: string
  done: boolean
  filled: number
  total: number
}

interface MealtimeStepListProps {
  steps: ReadonlyArray<MealtimeStepListItem>
  selectedKey: string
  onSelect: (key: string) => void
  orientation?: 'vertical' | 'horizontal'
}

/** The sub-label under a step: `Done` once signed, else the live progress. Pure. */
export function mealtimeStepSubLabel(
  step: Pick<MealtimeStepListItem, 'done' | 'filled' | 'total'>,
  orientation: 'vertical' | 'horizontal',
): string {
  if (orientation === 'horizontal') {
    return `${step.filled}/${step.total}`
  }
  return step.done ? 'Done' : `${step.filled}/${step.total} filled`
}

export function MealtimeStepList({ steps, selectedKey, onSelect, orientation = 'vertical' }: MealtimeStepListProps) {
  return (
    <WorkflowStepList
      steps={steps.map((step) => ({
        id: step.key,
        title: step.title,
        done: step.done,
        subLabel: mealtimeStepSubLabel(step, orientation),
      }))}
      selectedId={selectedKey}
      onSelect={onSelect}
      orientation={orientation}
      ariaLabel="Mealtimes"
    />
  )
}
